import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Users, Map, Calendar, Settings, Menu, Building2, Briefcase, Shield, FileText } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const AdminSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { isAdmin, hasPermission } = useAuth();

  // Each nav item can have:
  // - adminOnly: true — only visible to admins
  // - permission: 'scope' — visible to admins and moderators with that permission
  // If neither is set, it's visible to everyone who can access the admin panel
  const allNav = [
    { label: "Dashboard", to: "/admin", icon: <Home className="w-5 h-5" />, adminOnly: true },
    { label: "Users", to: "/admin/users", icon: <Users className="w-5 h-5" />, permission: "user_management" },
    { label: "Routes", to: "/admin/routes", icon: <Map className="w-5 h-5" />, permission: "route_management" },
    { label: "Events", to: "/admin/events", icon: <Calendar className="w-5 h-5" />, adminOnly: true },
    { label: "Organizers", to: "/admin/organizers", icon: <Building2 className="w-5 h-5" />, permission: "organizer_management" },
    { label: "Sponsors", to: "/admin/sponsors", icon: <Briefcase className="w-5 h-5" />, permission: "sponsor_management" },
    { label: "Moderators", to: "/admin/moderators", icon: <Shield className="w-5 h-5" />, adminOnly: true },
    { label: "Audit Logs", to: "/admin/audit", icon: <FileText className="w-5 h-5" />, adminOnly: true },
    { label: "Settings", to: "/admin/settings", icon: <Settings className="w-5 h-5" />, adminOnly: true },
  ];

  // Filter nav items based on role and permissions
  const nav = allNav.filter((item) => {
    if (isAdmin) return true;          // Admin sees everything
    if (item.adminOnly) return false;   // Moderator can't see admin-only items
    return hasPermission(item.permission); // Moderator sees items matching their permissions
  });

  const isActive = (path) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`flex flex-col bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 transition-all duration-200 ${
        collapsed ? "w-20" : "w-64"
      }`}
    >
      <div className="h-16 flex items-center px-4">
        <button
          onClick={() => setCollapsed((s) => !s)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <Menu className="w-5 h-5 text-gray-700 dark:text-gray-200" />
        </button>
        {!collapsed && (
          <div className="ml-3 font-semibold text-gray-800 dark:text-gray-100">
            {isAdmin ? "Koset Admin" : "Koset Moderator"}
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1">
        {nav.map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 p-2 rounded transition-colors ${
                active
                  ? "bg-green-50 text-green-700 dark:bg-gray-700 dark:text-green-400"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <span className="w-6 h-6 flex items-center justify-center">
                {item.icon}
              </span>
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

export default AdminSidebar;
